import React from 'react';
import { Alert, Button, Space, Typography } from 'antd';
import { BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import { Session } from 'state';

import SettingsCard from '../../../TrainerSession/SessionSettingsShared/components/SettingsCard';
import Field from '../../../TrainerSession/SessionSettingsShared/components/Field';
import BlueprintPremiumModelSettingsCard from './BlueprintPremiumModelSettingsCard';
import BlueprintMarketsViewSettingsCard from './BlueprintMarketsViewSettingsCard';

type Props = {
  displayNames?: React.ReactNode;
  sessionId: number;
  whichMarkets: Session['which_markets'];
  readOnly?: boolean;
  onOpenDiagnostics: () => void;
  historical: boolean;
  coverageActionWaiting?: boolean;
  onCoverageAction: () => void;
  onWhichMarketsChange: (value: Session['which_markets']) => void;
};

/** Blueprint-only controls integrated as a normal settings workspace view. */
const BlueprintToolsView: React.FC<Props> = ({
  displayNames,
  sessionId,
  whichMarkets,
  readOnly,
  onOpenDiagnostics,
  historical,
  coverageActionWaiting,
  onCoverageAction,
  onWhichMarketsChange,
}) => (
  <div className="bp-settings-v2__tools">
    {readOnly ? (
      <Alert
        type="warning"
        showIcon
        message="This blueprint version is locked"
        description="Its configuration is frozen. Performance-benchmark coverage diagnostics remain available; pricing is read-only."
      />
    ) : null}
    <div className="bp-settings-v2__tools-grid">
      {displayNames}
      {/* The session PERFORMANCE benchmark: the index/fund composition a
          trainee's P&L is measured against. Named in full on every label
          because the answer key (the marking scheme) now lives on the same
          blueprint page, and a bare "benchmark" could mean either. */}
      <SettingsCard title="Performance benchmark">
        <Field
          label="Performance benchmark coverage"
          hint="Inspect coverage across the template's instruments, performance benchmark and required historical dates."
        >
          <Space wrap>
            <Button
              type="primary"
              icon={<BarChartOutlined />}
              onClick={onOpenDiagnostics}
            >
              Open diagnostics
            </Button>
            <Button
              icon={<ReloadOutlined />}
              loading={coverageActionWaiting}
              onClick={onCoverageAction}
            >
              {historical
                ? 'Recompute performance-benchmark coverage'
                : 'Refresh coverage'}
            </Button>
          </Space>
        </Field>
        <Typography.Text type="secondary">
          Diagnostics refresh each time the drawer opens.{' '}
          {historical
            ? 'Recompute publishes the latest eligible funds and equities after instrument, window, schedule or market-history changes.'
            : 'Refresh rechecks coverage over the dates elapsed in this real-time template.'}
        </Typography.Text>
      </SettingsCard>
      <BlueprintPremiumModelSettingsCard
        sessionId={sessionId}
        readOnly={readOnly}
      />
      <BlueprintMarketsViewSettingsCard
        value={whichMarkets}
        readOnly={readOnly}
        onChange={onWhichMarketsChange}
      />
    </div>
  </div>
);

export default BlueprintToolsView;
